import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { LockKeyhole, LockKeyholeOpen, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { demoEndStepUp, demoStepUp } from '../lib/demo-state';
import { tm } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { authCall, RpcError } from '../lib/rpc';
import { Button, ErrorBox, Field, Input, Loading, Modal, Panel, Pill, Select, useToast } from './ui';

/** Areas behind the additional confirmation: the two modules and the money outside them (D-04). */
export type StepUpArea = 'billing' | 'customers' | 'finance';

// the confirmation dialog for calls outside the module pages (money of rates, analyses, complete exports)
const hostListeners = new Set<(area: StepUpArea) => void>();
let gatesMounted = 0;

/** Opens the confirmation dialog (when no module gate is on the screen, the gate asks itself). */
export function requestStepUp(area: StepUpArea = 'finance') {
  hostListeners.forEach((fn) => fn(area));
}

/** A module gate is on the screen: it shows the confirmation itself. */
export function stepUpGateActive() {
  return gatesMounted > 0;
}

/**
 * Additional confirmation for Abrechnung and Kunden (request 31 of round 4; mandatory since round 5, decision A-16). The
 * module is locked until the person confirms again: the authenticator code when one is set up, otherwise the password
 * (demo: the word "demo"). The confirmation holds the set minutes; the server checks it on every call of the module; this
 * gate only asks for it. Who holds no right to the module never reaches it (the menu hides it, the server refuses).
 */
export function StepUpGate({ area, children }: { area: 'billing' | 'customers'; children: ReactNode }) {
  const { t } = useTranslation();
  const q = useQ('me.stepUp', {}, { refetchInterval: 60_000 });
  useEffect(() => {
    gatesMounted++;
    return () => {
      gatesMounted--;
    };
  }, []);
  if (q.isLoading) return <Loading />;
  if (!q.data || q.data.fresh) return <>{children}</>;
  return (
    <div className="mx-auto mt-10 w-full max-w-md">
      <Panel title={t('stepUp.title')}>
        <StepUpForm area={area} minutes={q.data.minutes} />
      </Panel>
    </div>
  );
}

/** The confirmation form: method of the account, code or password, errors (lock after failed attempts explained). */
function StepUpForm({ area, minutes, onDone }: { area: StepUpArea; minutes: number; onDone?: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [method, setMethod] = useState<'password' | 'totp' | null | undefined>(__DEMO__ ? 'password' : undefined);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (__DEMO__) return;
    authCall<{ stepUpMethod?: 'password' | 'totp' | null }>('session', undefined, 'GET')
      .then((s) => setMethod(s.stepUpMethod ?? null))
      .catch(() => setMethod(null));
  }, []);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (__DEMO__) {
        if (!demoStepUp(value)) throw new RpcError('INVALID_CREDENTIALS');
      } else {
        await authCall('step-up', method === 'totp' ? { totp: value.trim() } : { password: value });
      }
      setValue('');
      // everything that was refused or left out before is loaded again
      await qc.invalidateQueries();
      onDone?.();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const lockedOut = error instanceof RpcError && error.code === 'ACCOUNT_LOCKED';
  return (
    <div className="flex flex-col gap-3">
      <p className="flex items-start gap-2 text-sm text-graphite">
        <LockKeyhole className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
        {area === 'finance' ? t('stepUp.textFinance', { minutes }) : t('stepUp.text', { module: t(`nav.${area}`), minutes })}
      </p>
      {method === undefined && <Loading />}
      {method === null && <p className="text-sm text-warn">{t('stepUp.unavailable')}</p>}
      {method && (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label={method === 'totp' ? t('stepUp.totp') : t('stepUp.password')} hint={__DEMO__ ? t('stepUp.demoHint') : undefined}>
            <Input
              autoFocus
              type={method === 'totp' ? 'text' : 'password'}
              inputMode={method === 'totp' ? 'numeric' : undefined}
              autoComplete={method === 'totp' ? 'one-time-code' : 'current-password'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </Field>
          <ErrorBox error={error} />
          {lockedOut && <p className="text-sm text-warn">{t('stepUp.lockedHelp')}</p>}
          <Button type="submit" variant="primary" loading={busy} disabled={!value.trim()} icon={<ShieldCheck className="size-4" />}>
            {t('stepUp.confirm')}
          </Button>
        </form>
      )}
      <p className="text-xs text-graphite">{t('stepUp.attempts')}</p>
      <details className="text-xs text-graphite">
        <summary className="cursor-pointer select-none">{t('stepUp.limitsTitle')}</summary>
        <p className="mt-1">{t('stepUp.limits')}</p>
      </details>
    </div>
  );
}

/** The confirmation dialog outside the module pages (mounted once by the app). */
export function StepUpHost() {
  const { t } = useTranslation();
  const [area, setArea] = useState<StepUpArea | null>(null);
  const q = useQ('me.stepUp', {}, { enabled: area !== null });
  useEffect(() => {
    const fn = (a: StepUpArea) => setArea(a);
    hostListeners.add(fn);
    return () => {
      hostListeners.delete(fn);
    };
  }, []);
  return (
    <Modal open={area !== null} onClose={() => setArea(null)} title={t('stepUp.title')}>
      {area && <StepUpForm area={area} minutes={q.data?.minutes ?? 15} onDone={() => setArea(null)} />}
    </Modal>
  );
}

/**
 * Notice where amounts are left out because the confirmation is missing (D-04: rates, costs and sales of the analyses,
 * the money of a project). The person holds the right; one click asks for the confirmation and the amounts appear.
 */
export function MoneyLockNotice({ locked, className }: { locked: boolean | undefined; className?: string }) {
  const { t } = useTranslation();
  if (!locked) return null;
  return (
    <div className={clsx('flex flex-wrap items-center gap-2 rounded-md border border-line bg-sheet-2 px-3 py-2 text-sm', className)}>
      <LockKeyhole className="size-4 text-graphite" aria-hidden />
      <span className="text-graphite">{t('stepUp.moneyLocked')}</span>
      <Button size="sm" icon={<ShieldCheck className="size-3.5" />} onClick={() => requestStepUp('finance')}>
        {t('stepUp.showMoney')}
      </Button>
    </div>
  );
}

/**
 * State of the confirmation for the person (request 41): locked, or open until a time, with "lock now". Shown on the
 * module pages next to the settings so that nobody wonders whether the protection is on.
 */
export function StepUpState() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQ('me.stepUp', {}, { refetchInterval: 60_000 });
  const [busy, setBusy] = useState(false);
  if (!q.data) return null;
  const end = async () => {
    setBusy(true);
    try {
      if (__DEMO__) demoEndStepUp();
      else await authCall('step-up/end', {});
      await qc.invalidateQueries();
      toast.ok(t('stepUp.lockedNow'));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  if (!q.data.fresh) {
    return (
      <Pill tone="neutral">
        <LockKeyhole className="size-3" /> {t('stepUp.stateLocked')}
      </Pill>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Pill tone="good" title={t('stepUp.stateHint')}>
        <LockKeyholeOpen className="size-3" /> {t('stepUp.stateOpen', { time: q.data.validUntil ? tm(q.data.validUntil) : '' })}
      </Pill>
      <Button size="sm" variant="ghost" loading={busy} icon={<LockKeyhole className="size-3.5" />} onClick={() => void end()}>
        {t('stepUp.lockNow')}
      </Button>
    </span>
  );
}

/**
 * How long a confirmation holds (holders of the module rights only, never the technical administration). The protection
 * itself is always on (A-16); a change of the time needs a fresh confirmation and is logged.
 */
export function StepUpSettings() {
  const { t } = useTranslation();
  const toast = useToast();
  const q = useQ('me.stepUp', {});
  const set = useM('security.setStepUp', { invalidate: ['me.stepUp'] });
  if (!q.data) return null;
  const d = q.data;
  return (
    <details className="rounded-lg border border-line bg-sheet px-3 py-2 text-sm">
      <summary className="flex cursor-pointer flex-wrap items-center gap-2 font-medium select-none">
        <LockKeyhole className="inline size-4 text-graphite" aria-hidden />
        {t('stepUp.settingsTitle')}
        <StepUpState />
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        <p className="text-xs text-graphite">{t('stepUp.mandatory')}</p>
        {d.canChange && (
          <Field label={t('stepUp.minutes')} className="w-48">
            <Select value={d.minutes} onChange={(e) => void set.mutateAsync({ minutes: Number(e.target.value) }).then(() => toast.ok(t('common.saved'))).catch(toast.error)}>
              {[5, 15, 30, 60, 120, 240].map((m) => (
                <option key={m} value={m}>
                  {t('stepUp.minutesValue', { count: m })}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <p className="text-xs text-graphite">{t('stepUp.settingsHint')}</p>
        <p className="text-xs text-graphite">{t('stepUp.attempts')}</p>
        <p className="text-xs text-graphite">{t('stepUp.limits')}</p>
      </div>
    </details>
  );
}
